import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  Download,
  Eye,
  Loader2,
  X,
  FileText,
  Stamp,
  RotateCcw,
  AlertCircle,
  CheckCircle2,
  Trash2,
} from 'lucide-react'
import { apiGet, apiPost, apiDelete, formatMXN } from '../lib/api'

/** Estados de una solicitud de factura según el contrato del backend. */
type FacturaStatus = 'pending' | 'stamped' | 'error' | 'cancelled'

interface FacturaRow {
  id: string
  orderNumber: string
  rfc: string
  razonSocial: string
  status: FacturaStatus | string
  uuid: string | null
  total: number
  createdAt: string
  pdfUrl: string | null
  xmlUrl: string | null
  errorMessage: string | null
}

interface FacturasData {
  total: number
  page: number
  pageSize: number
  pages: number
  data: FacturaRow[]
}

const STATUS_OPTIONS: Array<{ value: string; label: string }> = [
  { value: '', label: 'Todos los estados' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'stamped', label: 'Timbrada' },
  { value: 'error', label: 'Error' },
  { value: 'cancelled', label: 'Cancelada' },
]

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'stamped') return <Badge variant="success">Timbrada</Badge>
  if (low === 'error') return <Badge variant="danger">Error</Badge>
  if (low === 'cancelled') return <Badge variant="default">Cancelada</Badge>
  if (low === 'pending') return <Badge variant="warning">Pendiente</Badge>
  return <Badge>{s || 'Sin estado'}</Badge>
}

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return iso
  }
}

function readInitialFilters() {
  if (typeof window === 'undefined') return { page: 1, status: '' }
  const sp = new URLSearchParams(window.location.search)
  return {
    page: Math.max(1, parseInt(sp.get('page') || '1') || 1),
    status: sp.get('status') || '',
  }
}

export function Facturas() {
  const qc = useQueryClient()
  const initial = useMemo(readInitialFilters, [])
  const [page, setPage] = useState(initial.page)
  const [status, setStatus] = useState(initial.status)
  const [detalleId, setDetalleId] = useState<string | null>(null)
  const [flash, setFlash] = useState<{ tipo: 'ok' | 'err'; msg: string } | null>(null)

  // Sync URL params para que la pantalla sea linkeable / recargable.
  useEffect(() => {
    if (typeof window === 'undefined') return
    const sp = new URLSearchParams()
    if (page > 1) sp.set('page', String(page))
    if (status) sp.set('status', status)
    const qs = sp.toString()
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? '?' + qs : ''}`)
  }, [page, status])

  const queryString = useMemo(() => {
    const sp = new URLSearchParams()
    sp.set('page', String(page))
    if (status) sp.set('status', status)
    return sp.toString()
  }, [page, status])

  const { data, isLoading, isFetching } = useQuery<FacturasData>({
    queryKey: ['facturas', page, status],
    queryFn: () => apiGet(`/api/admin/facturas?${queryString}`),
    placeholderData: (prev) => prev,
  })

  function showFlash(tipo: 'ok' | 'err', msg: string) {
    setFlash({ tipo, msg })
    setTimeout(() => setFlash(null), 4000)
  }

  const timbrarMut = useMutation({
    mutationFn: (id: string) => apiPost<FacturaRow>(`/api/admin/facturas/${id}/timbrar`),
    onSuccess: (r) => {
      const ok = (r?.status || '').toLowerCase() === 'stamped'
      showFlash(
        ok ? 'ok' : 'err',
        ok ? 'Factura timbrada correctamente' : r?.errorMessage || 'No se pudo timbrar la factura',
      )
      qc.invalidateQueries({ queryKey: ['facturas'] })
      qc.invalidateQueries({ queryKey: ['factura', r?.id] })
    },
    onError: (e: any) => showFlash('err', e?.message || 'Error al timbrar'),
  })

  const cancelarMut = useMutation({
    mutationFn: (id: string) => apiDelete(`/api/admin/facturas/${id}`),
    onSuccess: () => {
      showFlash('ok', 'Factura cancelada')
      qc.invalidateQueries({ queryKey: ['facturas'] })
      setDetalleId(null)
    },
    onError: (e: any) => showFlash('err', e?.message || 'Error al cancelar'),
  })

  function clearFilters() {
    setStatus('')
    setPage(1)
  }

  return (
    <>
      <Topbar
        title="Facturas"
        subtitle={
          data ? `${data.total.toLocaleString('es-MX')} solicitudes de factura` : 'Cargando…'
        }
      />

      {flash && (
        <div
          className={`mx-6 mt-4 flex items-center gap-2 rounded-lg border px-3 py-2 text-sm ${
            flash.tipo === 'ok'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-red-200 bg-red-50 text-red-800'
          }`}
        >
          {flash.tipo === 'ok' ? <CheckCircle2 className="size-4" /> : <AlertCircle className="size-4" />}
          <span>{flash.msg}</span>
        </div>
      )}

      <div className="p-6">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value)
              setPage(1)
            }}
            className="rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          {status && (
            <button
              type="button"
              onClick={clearFilters}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <X className="size-3.5" /> Limpiar
            </button>
          )}
        </div>

        <div className="overflow-hidden rounded-2xl border border-ink-200 bg-white">
          {isLoading && (
            <div className="grid place-items-center py-20 text-ink-500">
              <Loader2 className="size-8 animate-spin" />
              <p className="mt-3 text-sm">Cargando facturas…</p>
            </div>
          )}

          {data && (
            <>
              <div className="relative overflow-x-auto">
                {isFetching && (
                  <div className="absolute right-3 top-3 z-10 rounded-full bg-white p-1.5 shadow-md">
                    <Loader2 className="size-4 animate-spin text-brand-600" />
                  </div>
                )}
                <table className="w-full text-sm">
                  <thead className="bg-ink-50">
                    <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
                      <th className="px-4 py-3 font-medium">Pedido</th>
                      <th className="px-4 py-3 font-medium">RFC / Razón social</th>
                      <th className="px-4 py-3 font-medium">Estado</th>
                      <th className="px-4 py-3 font-medium">Fecha</th>
                      <th className="px-4 py-3 font-medium">UUID</th>
                      <th className="px-4 py-3 font-medium text-right">Total</th>
                      <th className="px-4 py-3 font-medium text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.data.length === 0 && (
                      <tr>
                        <td colSpan={7} className="px-4 py-12 text-center text-sm text-ink-500">
                          No hay solicitudes de factura con esos filtros.
                        </td>
                      </tr>
                    )}
                    {data.data.map((f) => {
                      const low = (f.status || '').toLowerCase()
                      const canTimbrar = low === 'pending' || low === 'error'
                      const canDownload = low === 'stamped'
                      return (
                        <tr key={f.id} className="border-t border-ink-100 transition-colors hover:bg-ink-50">
                          <td className="px-4 py-3 font-mono text-sm font-medium text-ink-900">
                            #{f.orderNumber}
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-mono text-xs font-medium text-ink-900">{f.rfc}</div>
                            <div className="truncate text-xs text-ink-500">{f.razonSocial}</div>
                          </td>
                          <td className="px-4 py-3">
                            {statusBadge(f.status)}
                            {low === 'error' && f.errorMessage && (
                              <div className="mt-1 max-w-[220px] truncate text-[11px] text-red-600" title={f.errorMessage}>
                                {f.errorMessage}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-ink-700">{fmtDate(f.createdAt)}</td>
                          <td className="px-4 py-3">
                            {f.uuid ? (
                              <span className="font-mono text-[11px] text-ink-600" title={f.uuid}>
                                {f.uuid.slice(0, 8)}…
                              </span>
                            ) : (
                              <span className="text-xs text-ink-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-ink-900">
                            {formatMXN(f.total)}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center justify-end gap-1">
                              {canTimbrar && (
                                <button
                                  onClick={() => timbrarMut.mutate(f.id)}
                                  disabled={timbrarMut.isPending}
                                  className="inline-flex items-center gap-1 rounded-md border border-ink-200 bg-white px-2 py-1 text-xs font-medium text-ink-700 hover:bg-ink-100 disabled:opacity-50"
                                  title={low === 'error' ? 'Reintentar timbrado' : 'Timbrar factura'}
                                >
                                  {timbrarMut.isPending && timbrarMut.variables === f.id ? (
                                    <Loader2 className="size-3.5 animate-spin" />
                                  ) : low === 'error' ? (
                                    <RotateCcw className="size-3.5" />
                                  ) : (
                                    <Stamp className="size-3.5" />
                                  )}
                                  {low === 'error' ? 'Reintentar' : 'Timbrar'}
                                </button>
                              )}
                              {canDownload && f.pdfUrl && (
                                <a
                                  href={f.pdfUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                  title="Descargar PDF"
                                >
                                  <FileText className="size-4" />
                                </a>
                              )}
                              {canDownload && f.xmlUrl && (
                                <a
                                  href={f.xmlUrl}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                  title="Descargar XML"
                                >
                                  <Download className="size-4" />
                                </a>
                              )}
                              <button
                                onClick={() => setDetalleId(f.id)}
                                className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                                title="Ver detalle"
                              >
                                <Eye className="size-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between border-t border-ink-200 bg-ink-50 px-4 py-3 text-sm">
                <div className="text-ink-500">
                  Página <strong className="text-ink-900">{data.page}</strong> de{' '}
                  <strong className="text-ink-900">{data.pages.toLocaleString('es-MX')}</strong>{' '}
                  ({data.total.toLocaleString('es-MX')} facturas)
                </div>
                <div className="flex gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <button className="rounded-md bg-brand-600 px-3 py-1 text-white">{page}</button>
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= data.pages}
                    className="rounded-md border border-ink-200 bg-white px-3 py-1 hover:bg-ink-100 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {detalleId && (
        <FacturaDetalle
          id={detalleId}
          onClose={() => setDetalleId(null)}
          onTimbrar={(id) => timbrarMut.mutate(id)}
          timbrando={timbrarMut.isPending}
          onCancelar={(id) => cancelarMut.mutate(id)}
          cancelando={cancelarMut.isPending}
        />
      )}
    </>
  )
}

/* ---------- Slide-over de detalle ---------- */

function FacturaDetalle({
  id,
  onClose,
  onTimbrar,
  timbrando,
  onCancelar,
  cancelando,
}: {
  id: string
  onClose: () => void
  onTimbrar: (id: string) => void
  timbrando: boolean
  onCancelar: (id: string) => void
  cancelando: boolean
}) {
  const { data: f, isLoading } = useQuery<FacturaRow>({
    queryKey: ['factura', id],
    queryFn: () => apiGet(`/api/admin/facturas/${id}`),
  })

  const low = (f?.status || '').toLowerCase()
  const canTimbrar = low === 'pending' || low === 'error'
  const canDownload = low === 'stamped'

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/30" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-md flex-col overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-ink-200 px-5 py-4">
          <div>
            <h2 className="text-lg font-semibold text-ink-900">Detalle de factura</h2>
            {f && <p className="text-xs text-ink-500">Pedido #{f.orderNumber}</p>}
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-5" />
          </button>
        </div>

        <div className="flex-1 p-5">
          {isLoading && (
            <div className="flex items-center gap-2 text-ink-500">
              <Loader2 className="size-4 animate-spin" /> Cargando…
            </div>
          )}
          {f && (
            <dl className="space-y-3 text-sm">
              <Row label="Estado">{statusBadge(f.status)}</Row>
              <Row label="Pedido">#{f.orderNumber}</Row>
              <Row label="RFC">
                <span className="font-mono">{f.rfc}</span>
              </Row>
              <Row label="Razón social">{f.razonSocial}</Row>
              <Row label="Total">{formatMXN(f.total)}</Row>
              <Row label="Fecha">{fmtDate(f.createdAt)}</Row>
              <Row label="UUID">
                {f.uuid ? (
                  <span className="break-all font-mono text-xs">{f.uuid}</span>
                ) : (
                  <span className="text-ink-400">—</span>
                )}
              </Row>
              {low === 'error' && f.errorMessage && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                  <strong>Error de timbrado:</strong> {f.errorMessage}
                </div>
              )}
              {canDownload && (
                <div className="flex gap-2 pt-1">
                  {f.pdfUrl && (
                    <a
                      href={f.pdfUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
                    >
                      <FileText className="size-4" /> PDF
                    </a>
                  )}
                  {f.xmlUrl && (
                    <a
                      href={f.xmlUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
                    >
                      <Download className="size-4" /> XML
                    </a>
                  )}
                </div>
              )}
            </dl>
          )}
        </div>

        {f && (
          <div className="flex flex-wrap gap-2 border-t border-ink-200 px-5 py-4">
            {canTimbrar && (
              <button
                onClick={() => onTimbrar(f.id)}
                disabled={timbrando}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {timbrando ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : low === 'error' ? (
                  <RotateCcw className="size-3.5" />
                ) : (
                  <Stamp className="size-3.5" />
                )}
                {low === 'error' ? 'Reintentar timbrado' : 'Timbrar'}
              </button>
            )}
            {low !== 'cancelled' && (
              <button
                onClick={() => {
                  if (confirm('¿Cancelar esta factura? Esta acción no se puede deshacer.')) {
                    onCancelar(f.id)
                  }
                }}
                disabled={cancelando}
                className="inline-flex items-center gap-1.5 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                {cancelando ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                Cancelar
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 border-b border-ink-100 pb-2">
      <dt className="shrink-0 text-xs font-medium uppercase tracking-wide text-ink-500">{label}</dt>
      <dd className="text-right text-ink-900">{children}</dd>
    </div>
  )
}
